import { Checkbox } from "@xola/ui-kit";
import clsx from "clsx";
import React, { useEffect, useMemo, useState } from "react";
import { SelectionState } from "types";
import { useDebounce } from "use-debounce";
import { useInfiniteCollection } from "../../../../hooks/useInfiniteCollection";
import { useSearch } from "../../../../hooks/useSearch";
import { Item, useSelection } from "../../../../hooks/useSelection";
import { InfiniteList } from "../../../Common/InfiniteList";
import { SearchInput } from "../../../Common/SearchInput";
import { useSeller } from "@/hooks/useAccount";

interface CustomFilterSelectorProps {
    value: SelectionState;
    onChange: (value: SelectionState) => void;
    collection: string;
    enableSelectAll?: boolean;
    enableEmptySelection?: boolean;
}

const defaultValue: SelectionState = {
    all: true,
    selected: [],
};

export const CustomFilterSelector = ({
    value = defaultValue,
    onChange,
    collection,
    enableSelectAll = true,
    enableEmptySelection = false,
}: CustomFilterSelectorProps) => {
    const seller = useSeller();
    const [searchTerm, setSearchTerm] = useState("");
    const [isEmptyChecked, setIsEmptyChecked] = useState<boolean>(true);
    const [name] = useDebounce(searchTerm, 500);

    const fetchParams = {
        name: name || null,
        seller: seller.id,
        source: "xola",
    };

    const {
        data: items,
        hasNextPage = false,
        fetchNextPage,
        isFetchingNextPage,
    } = useInfiniteCollection<any>(collection, fetchParams, { keepPreviousData: true });

    const filteredItems = useSearch(items, (item: any) => [item.name], searchTerm);

    const isFiltered = searchTerm.length > 0;
    const isSubset = enableSelectAll ? isFiltered : true;
    const defaultSelection = useMemo(() => ({ all: value.all, selected: value.selected }), [value]);
    const selection = useSelection(filteredItems as Item[], isSubset, defaultSelection);

    useEffect(() => {
        onChange({
            all: selection.state.all,
            selected: selection.state.selected,
            ...(enableEmptySelection && { isEmpty: isEmptyChecked }),
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selection.state, isEmptyChecked]);

    const handleSearchChange = (searchResult: string) => {
        setSearchTerm(searchResult.trim());
    };

    const handleEmptyToggle = () => {
        setIsEmptyChecked((previous) => !previous);
    };

    return (
        <div className="ui-product-selector overflow-hidden rounded-lg border border-gray-lighter">
            {enableSelectAll ? (
                <div className="flex items-center justify-between bg-gray-lighter py-3 px-6">
                    <Checkbox
                        checked={selection.isSelectedAll}
                        label="Select all"
                        onChange={() => selection.selectAll()}
                    />
                    <span className="text-sm font-bold text-primary">{selection.count} selected</span>
                </div>
            ) : null}
            {enableEmptySelection ? (
                <div className="flex items-center justify-between bg-gray-lighter py-3 px-6">
                    <Checkbox checked={isEmptyChecked} label="Is Empty" onChange={handleEmptyToggle} />
                </div>
            ) : null}
            <div className="bg-white p-3">
                <SearchInput className="w-full" onChange={handleSearchChange} />
                <InfiniteList
                    hasMore={hasNextPage ?? false}
                    isLoading={isFetchingNextPage}
                    className={clsx(
                        "h-60 divide-y divide-gray-light overflow-y-auto overflow-x-hidden rounded border border-gray-light bg-white",
                    )}
                    onLoadMore={fetchNextPage}
                >
                    {filteredItems.map(
                        (item) =>
                            item.id && (
                                <FilterItem
                                    key={item.id}
                                    item={item}
                                    checked={selection.isSelected(item as Item)}
                                    onChange={() => {
                                        selection.select(item as Item);
                                    }}
                                />
                            ),
                    )}
                </InfiniteList>
            </div>
        </div>
    );
};

interface FilterItemProps {
    item: any;
    checked: boolean;
    onChange: () => void;
}

const FilterItem = ({ item, checked, onChange }: FilterItemProps) => {
    return (
        <Checkbox
            key={item.id}
            checked={checked}
            name={item.id}
            label={<span className={clsx("my-2 flex items-center hover:cursor-pointer")}>{item.name?.trim()}</span>}
            className={clsx("px-3 py-2 hover:cursor-pointer", checked ? "bg-blue-lighter" : "hover:bg-gray-hover")}
            classNames={{ checkbox: "!border !border-gray" }}
            onChange={onChange}
        />
    );
};
